const hasValue = (value) => value !== null && value !== undefined && value !== '';
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);

const LOCATION_NOISE = /\b(hub|facility|warehouse|godown|depot|centre|center|nsh|ich|sorting|office|scan|arrived|reached|location)\b/gi;

const uniqueQueries = (queries) => queries
  .filter(hasValue)
  .map((query) => String(query).trim())
  .filter((query, index, all) => query && all.indexOf(query) === index);

const getPostalCode = (value) => {
  const postalCode = value?.pincode || value?.pinCode || value?.pin_code
    || value?.postalCode || value?.postal_code || value?.postcode || value?.zipcode || value?.zip;
  const normalized = String(postalCode || '').trim();
  return normalized || null;
};

const getCity = (value) => {
  const city = value?.city || value?.cityName || value?.district || value?.town;
  const normalized = String(city || '').trim();
  return normalized || null;
};

const getState = (value) => String(value?.state || value?.province || value?.provinceCode || '').trim() || null;

export const getCountryCode = (value) => {
  const raw = String(value?.country || value?.countryCode || value?.countryCodeV2 || 'IN').trim().toUpperCase();
  if (!raw || raw === 'INDIA' || raw === 'IND') return 'IN';
  return raw.length === 2 ? raw : 'IN';
};

const INDIAN_STATE_CODES = {
  'ANDAMAN AND NICOBAR ISLANDS': 'AN',
  'ANDHRA PRADESH': 'AP',
  'ARUNACHAL PRADESH': 'AR',
  'ASSAM': 'AS',
  'BIHAR': 'BR',
  'CHANDIGARH': 'CH',
  'CHHATTISGARH': 'CG',
  'DADRA AND NAGAR HAVELI AND DAMAN AND DIU': 'DH',
  'DELHI': 'DL',
  'GOA': 'GA',
  'GUJARAT': 'GJ',
  'HARYANA': 'HR',
  'HIMACHAL PRADESH': 'HP',
  'JAMMU AND KASHMIR': 'JK',
  'JHARKHAND': 'JH',
  'KARNATAKA': 'KA',
  'KERALA': 'KL',
  'LADAKH': 'LA',
  'LAKSHADWEEP': 'LD',
  'MADHYA PRADESH': 'MP',
  'MAHARASHTRA': 'MH',
  'MANIPUR': 'MN',
  'MEGHALAYA': 'ML',
  'MIZORAM': 'MZ',
  'NAGALAND': 'NL',
  'NCT OF DELHI': 'DL',
  'NEW DELHI': 'DL',
  'ODISHA': 'OD',
  'ORISSA': 'OD',
  'PUDUCHERRY': 'PY',
  'PONDICHERRY': 'PY',
  'PUNJAB': 'PB',
  'RAJASTHAN': 'RJ',
  'SIKKIM': 'SK',
  'TAMIL NADU': 'TN',
  'TELANGANA': 'TS',
  'TRIPURA': 'TR',
  'UTTAR PRADESH': 'UP',
  'UTTARAKHAND': 'UK',
  'WEST BENGAL': 'WB',
};

export function toStateCode(state, countryCode = 'IN') {
  const raw = String(state || '').trim();
  if (!raw) return null;
  if (/^[A-Za-z]{2}$/.test(raw)) return raw.toUpperCase();
  if (String(countryCode || 'IN').toUpperCase() === 'IN') {
    return INDIAN_STATE_CODES[raw.toUpperCase()] || null;
  }
  return null;
}

export function formatCityStateCountry(value) {
  const record = typeof value === 'string' ? { city: value } : (isRecord(value) ? value : null);
  if (!record) return null;
  const city = getCity(record);
  const country = getCountryCode(record);
  const state = toStateCode(getState(record), country);
  if (!city && !state) return null;
  return [city, state, country].filter(hasValue).join(', ');
}

export function placeFromAddressComponents(components = []) {
  const pick = (type, useShort = false) => {
    const match = components.find((item) => Array.isArray(item.types) && item.types.includes(type));
    if (!match) return null;
    const value = useShort ? match.short_name : match.long_name;
    return String(value || '').trim() || null;
  };

  const city = pick('locality')
    || pick('postal_town')
    || pick('administrative_area_level_2')
    || pick('sublocality_level_1')
    || pick('sublocality');
  const countryShort = (pick('country', true) || '').toUpperCase();
  const country = countryShort.length === 2 ? countryShort : getCountryCode({ country: pick('country') });
  const stateShort = pick('administrative_area_level_1', true);
  const state = toStateCode(stateShort, country) || toStateCode(pick('administrative_area_level_1'), country);

  return {
    city: city || null,
    state: state || null,
    country: country || null,
  };
}

export const extractCityFromLabel = (label) => {
  if (!label || typeof label !== 'string') return null;

  const firstSegment = label.split(',')[0].trim();
  if (!firstSegment) return null;

  const cleaned = firstSegment
    .replace(LOCATION_NOISE, ' ')
    .replace(/[^A-Za-z .'-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return cleaned.length >= 2 ? cleaned : null;
};

const withPlaceContext = (place, value) => {
  const state = getState(value);
  const country = getCountryCode(value);
  return uniqueQueries([
    [place, state, country].filter(hasValue).join(', '),
    [place, country].filter(hasValue).join(', '),
    place,
  ]);
};

const toRecord = (value) => {
  if (typeof value === 'string') return { label: value.trim() };
  return isRecord(value) ? value : {};
};

/**
 * Approximate map queries for a shipmentMap stop.
 * Origin/destination: pincode, then city.
 * Current location: pincode, then city, then a city parsed from label.
 */
export function buildGeocodeQueries(value, kind = 'stop') {
  const record = toRecord(value);
  const postalCode = getPostalCode(record);
  const city = getCity(record);
  const queries = [];

  if (postalCode) {
    const country = getCountryCode(record);
    queries.push([postalCode, country === 'IN' ? 'India' : country].join(', '));
    queries.push(postalCode);
  }
  if (city) queries.push(...withPlaceContext(city, record));

  if (kind === 'current') {
    const label = typeof record.label === 'string' ? record.label.trim() : '';
    const labelCity = extractCityFromLabel(label);
    if (labelCity && labelCity.toLowerCase() !== String(city || '').toLowerCase()) {
      queries.push(...withPlaceContext(labelCity, record));
    }
    if (label && !postalCode && !city) queries.push(label);
  }

  return uniqueQueries(queries);
}

export function buildGeocodeRequest(query, country) {
  const address = String(query || '').trim();
  const countryCode = getCountryCode({ country });
  return {
    address,
    region: countryCode.toLowerCase(),
  };
}
